import { cn } from '@/lib/utils.js';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] text-sm font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-qup-primary)] text-white hover:bg-[var(--color-qup-primary-hover)]',
        secondary:
          'bg-white text-[var(--color-qup-text-light)] border border-[var(--color-qup-border-input)] hover:bg-[var(--color-qup-bg-light)]',
        ghost:
          'bg-transparent text-[var(--color-qup-primary)] hover:bg-[var(--color-qup-primary)]/10',
        danger:
          'bg-[var(--color-qup-primary)] text-white hover:bg-[var(--color-qup-primary-hover)]',
        outline:
          'border border-[var(--color-qup-border-dark)] bg-transparent text-[var(--color-qup-text-dark)] hover:bg-[var(--color-qup-bg-card-dark)]',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-6 py-2',
        lg: 'h-12 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);

Button.displayName = 'Button';

export { buttonVariants };
